import { useQuery, UseQueryResult } from 'react-query'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { DEFAULT_CATALOG_KEYS } from 'components/AiAvatarAnnouncementModal/types'

const POLL_INTERVAL_MS = 2000
const TIMEOUT_MS = 30_000

interface RecommendationStatus {
  status: 'processing' | 'ready' | 'failed'
  recommended_keys: string[] | null
  error: string | null
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const useRecommendAvatars = ({
  brandKitId,
  enabled,
}: {
  brandKitId?: number
  enabled: boolean
}): UseQueryResult<string[]> => {
  const workspaceId = useCurrentWorkspaceId()

  return useQuery<string[]>(
    ['workspaces', workspaceId, 'brandKits', brandKitId, 'avatars', 'recommendation'],
    async () => {
      const basePath = `/w/${workspaceId}/brand_kits/${brandKitId}/avatars`
      try {
        const startRes = await sendToApi<{ token: string }>(`${basePath}/recommend`, {
          method: 'POST',
        })
        if (startRes.type !== SUCCESS_RESPONSE_TYPE) return DEFAULT_CATALOG_KEYS

        const deadline = Date.now() + TIMEOUT_MS
        do {
          const res = await sendToApi<RecommendationStatus>(
            `${basePath}/recommendation_status?token=${startRes.data.token}`,
          )
          if (res.type !== SUCCESS_RESPONSE_TYPE) return DEFAULT_CATALOG_KEYS
          if (res.data.status === 'failed') return DEFAULT_CATALOG_KEYS
          if (res.data.status === 'ready') return res.data.recommended_keys ?? DEFAULT_CATALOG_KEYS
          await wait(POLL_INTERVAL_MS)
        } while (Date.now() < deadline)

        return DEFAULT_CATALOG_KEYS
      } catch {
        return DEFAULT_CATALOG_KEYS
      }
    },
    {
      enabled: enabled && !!brandKitId,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    },
  )
}
